/**
 * Runtime configuration, read from the global that web/config.js installs before the bundle runs.
 *
 * Nothing here is baked in at build time: the anon key is gitignored and the API origin differs
 * between dev and production, while the bundle is the same in both.
 */
export interface RuntimeConfig {
  /** Origin of the Supabase API. Empty = same origin as the portal, which is how Caddy serves it. */
  apiBaseUrl: string;
  /** The public anon JWT (`ANON_KEY` in .env). */
  anonKey: string;
  /** Forces the UI language instead of the browser's. */
  defaultLocale: string | null;
  /** `false` switches the Centrifugo connection off (everything polls). Absent = on. */
  realtime?: boolean;
  /** `false` hides "Continue with Google" (the kill switch). Absent = on. */
  googleSignIn?: boolean;
  /** `true` shows "Continue with Apple", once the web Service ID exists. Absent = off. */
  appleSignIn?: boolean;
  /** The address the "access paused" screen of a suspended agency offers. Absent = a generic line. */
  supportEmail?: string | null;
}

declare global {
  interface Window {
    __COREWAY_CONFIG__?: Partial<RuntimeConfig>;
  }
}

export class ConfigError extends Error {}

const PLACEHOLDER = 'replace-me-with-the-anon-key';

/**
 * Reads and validates the runtime config. Throws rather than falling back to a default: a portal
 * running against the wrong API with a silently empty key is worse than one that refuses to boot.
 */
export function loadConfig(): RuntimeConfig {
  const raw = window.__COREWAY_CONFIG__;
  if (!raw) {
    throw new ConfigError(
      'config.js did not load. Copy web/config.example.js to web/config.js and fill it in.',
    );
  }
  if (!raw.anonKey || raw.anonKey === PLACEHOLDER) {
    throw new ConfigError('config.js has no anonKey. Set it to the ANON_KEY value from .env.');
  }
  return {
    apiBaseUrl: raw.apiBaseUrl?.replace(/\/$/, '') || window.location.origin,
    anonKey: raw.anonKey,
    defaultLocale: raw.defaultLocale ?? null,
    realtime: raw.realtime !== false,
    googleSignIn: raw.googleSignIn !== false,
    appleSignIn: raw.appleSignIn === true,
    supportEmail: supportEmail(raw),
  };
}

/**
 * The support address of the runtime config, trimmed; null when absent, blank or not an address (a
 * typo in config.js must not render a broken mail link). Read straight from the global, like the
 * OAuth flags.
 */
export function supportEmail(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): string | null {
  const value = typeof raw?.supportEmail === 'string' ? raw.supportEmail.trim() : '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : null;
}

/**
 * The OAuth ways in this portal offers: Google unless switched off, Apple only when switched on. Read
 * straight from the global, like `realtime` (the flags are not plumbed through React).
 */
export function oauthProviders(raw: Partial<RuntimeConfig> | undefined = window.__COREWAY_CONFIG__): { google: boolean; apple: boolean } {
  return { google: raw?.googleSignIn !== false, apple: raw?.appleSignIn === true };
}

